import { useState, useEffect, useMemo, useCallback, useRef } from 'react';
import { useInput } from 'react-admin';
import type { Variant } from '@src/types';
import type { BlendedPhotographyImage } from '@src/settings';
import { buildScopeKeys, buildScopeKeysForReading } from './scopeKeyUtils';
import type { FilterDimension } from './scopeKeyUtils';

type ScopeImageMap = Record<string, Record<string, string>>;

const ALL_VALUE = '__all__';

/** Find the next available image name when a name collision occurs. */
export const nextAvailableName = (
  name: string,
  taken: Record<string, string>,
): string => {
  if (!(name in taken)) return name;
  const match = name.match(/^(.*?)(\d+)$/);
  const base = match ? match[1] : name;
  let num = match ? parseInt(match[2], 10) + 1 : 2;
  while (`${base}${num}` in taken) num++;
  return `${base}${num}`;
};

/**
 * Merge images from multiple scope keys, deduplicating by URL.
 * If two scope keys have different URLs under the same name (e.g. both have 'alt1'),
 * the second gets renamed (e.g. 'alt2') so all unique images are visible.
 */
export const mergeImages = (
  scopeKeys: string[],
  imageMap: ScopeImageMap | undefined,
): Record<string, string> => {
  if (scopeKeys.length === 0 || !imageMap) return {};
  const merged: Record<string, string> = {};
  const seenUrls = new Set<string>();

  for (const key of scopeKeys) {
    const images = imageMap[key];
    if (!images) continue;
    for (const [name, url] of Object.entries(images)) {
      if (seenUrls.has(url)) continue;
      seenUrls.add(url);
      merged[nextAvailableName(name, merged)] = url;
    }
  }
  return merged;
};

export type AssetSource = 'catalog' | 'studio' | 'video' | 'inventory';

export interface EligibleSlot {
  tag: string;
  required: boolean;
  catalogKey: string | null;
  uploadStorageType: 'catalog' | 'studio';
  url: string | null;
  displayKey: string | null;
  sourceType: AssetSource;
  hasInventorySource: boolean;
}

export interface IneligibleAsset {
  key: string;
  url: string;
  source: AssetSource;
}

interface UseMediaStateArgs {
  record: Record<string, unknown> | undefined;
  selectedVariant: Variant | null;
  selectedColorway: string;
  selectedFilters: Record<string, string>;
  filterDimensions: FilterDimension[];
  blendedPhotographyImages: BlendedPhotographyImage[];
  catalogOnlyMode: boolean;
}

export function useMediaState({
  record,
  selectedVariant,
  selectedColorway,
  selectedFilters,
  filterDimensions,
  blendedPhotographyImages,
  catalogOnlyMode,
}: UseMediaStateArgs) {
  // Form fields for partner images
  const { field: partnerImagesField } = useInput({ source: 'imagesByColorway' });
  const { field: partnerFilesField } = useInput({ source: 'files' });
  const { field: partnerDirtyField } = useInput({ source: 'partnerImagesDirty', defaultValue: false });

  // Form fields for studio images
  const { field: studioImagesField } = useInput({ source: 'studioImagesByColorway' });
  const { field: studioFilesField } = useInput({ source: 'studioFiles' });
  const { field: studioDirtyField } = useInput({ source: 'studioImagesDirty', defaultValue: false });

  // Form fields for videos
  const { field: videoImagesField } = useInput({ source: 'videosByColorway' });
  const { field: videoDirtyField } = useInput({ source: 'videoImagesDirty', defaultValue: false });

  // Local working copies — avoids mutating record directly
  const [workingImages, setWorkingImages] = useState<ScopeImageMap>({});
  const [workingStudioImages, setWorkingStudioImages] = useState<ScopeImageMap>({});
  const [workingVideos, setWorkingVideos] = useState<ScopeImageMap>({});
  const [workingFiles, setWorkingFiles] = useState<Record<string, File>>({});
  const [workingStudioFiles, setWorkingStudioFiles] = useState<Record<string, File>>({});

  // Track blob URLs so they can be revoked to avoid memory leaks
  const blobUrlsRef = useRef<Set<string>>(new Set());

  const createBlobUrl = useCallback((file: File): string => {
    const url = URL.createObjectURL(file);
    blobUrlsRef.current.add(url);
    return url;
  }, []);

  useEffect(() => {
    const urls = blobUrlsRef.current;
    return () => {
      urls.forEach(url => URL.revokeObjectURL(url));
      urls.clear();
    };
  }, []);

  // Sync from record whenever its data changes (new reference from react-query).
  // Using `record` rather than `record?.id` ensures we pick up full data when
  // react-admin initially returns a partial record from the getList cache
  // (without imagesByColorway) and then replaces it with the getOne response.
  useEffect(() => {
    if (record) {
      setWorkingImages((record.imagesByColorway as ScopeImageMap) || {});
      setWorkingStudioImages((record.studioImagesByColorway as ScopeImageMap) || {});
      setWorkingVideos((record.videosByColorway as ScopeImageMap) || {});
      setWorkingFiles((record.files as Record<string, File>) || {});
      setWorkingStudioFiles((record.studioFiles as Record<string, File>) || {});
    }
  }, [record]);

  // All SKUs that match the current filter combination
  const matchingSkus = useMemo(() => {
    if (!selectedVariant) return [];
    const hasAnyFilter = Object.entries(selectedFilters).some(
      ([, v]) => v && v !== ALL_VALUE,
    );
    if (!hasAnyFilter) return [];
    let indices = selectedVariant.skus.map((_, i) => i);
    for (const dim of filterDimensions) {
      const val = selectedFilters[dim.key];
      if (!val || val === ALL_VALUE) continue;
      indices = indices.filter(i => dim.allValues[i] === val);
    }
    return indices.map(i => selectedVariant.skus[i]);
  }, [selectedVariant, selectedFilters, filterDimensions]);

  const parentSku = (record?.id as string) || '';

  const scopeKeys = useMemo(
    () => buildScopeKeys(selectedColorway, selectedVariant, selectedFilters, filterDimensions, parentSku),
    [selectedColorway, selectedVariant, selectedFilters, filterDimensions, parentSku],
  );

  const scopeKeysForReading = useMemo(
    () => buildScopeKeysForReading(
      scopeKeys, matchingSkus, selectedVariant,
      workingImages, workingStudioImages, workingVideos,
    ),
    [scopeKeys, matchingSkus, selectedVariant, workingImages, workingStudioImages, workingVideos],
  );

  // Merged images per type
  const allImages = useMemo(
    () => mergeImages(scopeKeysForReading, workingImages),
    [scopeKeysForReading, workingImages],
  );
  const allStudioImages = useMemo(
    () => mergeImages(scopeKeysForReading, workingStudioImages),
    [scopeKeysForReading, workingStudioImages],
  );
  const allVideos = useMemo(
    () => mergeImages(scopeKeysForReading, workingVideos),
    [scopeKeysForReading, workingVideos],
  );

  // Eligible / ineligible slot computation
  const { eligibleSlots, ineligibleAssets } = useMemo(() => {
    const slots: EligibleSlot[] = [];
    const eligibleCatalogKeys = new Set<string>();
    const eligibleStudioKeys = new Set<string>();
    const eligibleVideoKeys = new Set<string>();

    for (const blendedImg of blendedPhotographyImages) {
      let hasInventorySource = false;
      const catalogSources: Array<{ key: string; storageType: 'catalog' | 'studio' | 'video' }> = [];

      for (const source of blendedImg.sources) {
        if (source.startsWith('catalog_items.')) {
          const parts = source.split('.');
          const collection = parts[1];
          const key = parts[2];
          if (collection && key) {
            const storageType = collection === 'partner_video_urls' ?
              'video' as const :
              (!catalogOnlyMode && collection === 'image_urls') ?
              'studio' as const :
              'catalog' as const;
            catalogSources.push({ key, storageType });
          }
        }
        if (source.startsWith('inventory_items.')) {
          hasInventorySource = true;
        }
      }

      if (catalogSources.length > 0) {
        const firstSource = catalogSources[0];

        for (const cs of catalogSources) {
          if (cs.storageType === 'video') eligibleVideoKeys.add(cs.key);
          else if (cs.storageType === 'studio') eligibleStudioKeys.add(cs.key);
          else eligibleCatalogKeys.add(cs.key);
        }

        let displayUrl: string | null = null;
        let displayKey: string | null = null;
        let displaySourceType: AssetSource = firstSource.storageType;

        for (const cs of catalogSources) {
          const map = cs.storageType === 'video' ?
            allVideos :
            cs.storageType === 'studio' ?
            allStudioImages :
            allImages;
          if (map[cs.key]) {
            displayUrl = map[cs.key];
            displayKey = cs.key;
            displaySourceType = cs.storageType;
            break;
          }
        }

        const uploadStorageType = firstSource.storageType === 'video' ?
          'catalog' as const :
          firstSource.storageType;

        slots.push({
          tag: blendedImg.tag,
          required: blendedImg.required,
          catalogKey: firstSource.key,
          uploadStorageType,
          url: displayUrl,
          displayKey,
          sourceType: displaySourceType,
          hasInventorySource,
        });
      } else if (hasInventorySource) {
        slots.push({
          tag: blendedImg.tag,
          required: blendedImg.required,
          catalogKey: null,
          uploadStorageType: 'catalog',
          url: null,
          displayKey: null,
          sourceType: 'inventory',
          hasInventorySource: true,
        });
      }
    }

    const ineligible: IneligibleAsset[] = [];
    for (const [key, url] of Object.entries(allImages)) {
      if (!eligibleCatalogKeys.has(key)) {
        ineligible.push({ key, url, source: 'catalog' });
      }
    }
    for (const [key, url] of Object.entries(allStudioImages)) {
      if (!eligibleStudioKeys.has(key)) {
        ineligible.push({ key, url, source: 'studio' });
      }
    }
    for (const [key, url] of Object.entries(allVideos)) {
      if (!eligibleVideoKeys.has(key)) {
        ineligible.push({ key, url, source: 'video' });
      }
    }

    return { eligibleSlots: slots, ineligibleAssets: ineligible };
  }, [allImages, allStudioImages, allVideos, blendedPhotographyImages, catalogOnlyMode]);

  // --- Change handlers ---

  const handleImagesChange = useCallback(
    (updatedImages: Record<string, string>, files: Record<string, File>) => {
      if (scopeKeys.length === 0 || !selectedVariant) return;

      setWorkingImages((prev) => {
        const next = { ...prev };
        for (const scopeKey of scopeKeysForReading) {
          next[scopeKey] = { ...updatedImages };
        }
        return next;
      });

      const scopeKeysForBackend: ScopeImageMap = {};
      for (const scopeKey of scopeKeys) {
        scopeKeysForBackend[scopeKey] = { ...updatedImages };
      }

      // Compute updated files eagerly so we can pass them to onChange
      // (setWorkingFiles is async, so workingFiles would be stale).
      // Only drop entries whose image key was actually removed from
      // updatedImages — a blanket delete-then-re-add would discard files
      // staged by earlier uploads that aren't part of this call's `files`.
      const updatedFiles = { ...workingFiles };
      for (const scopeKey of scopeKeys) {
        for (const key of Object.keys(updatedFiles)) {
          if (!key.startsWith(`${scopeKey}_`)) continue;
          const imageKey = key.slice(scopeKey.length + 1);
          if (!(imageKey in updatedImages)) delete updatedFiles[key];
        }
      }
      Object.entries(files).forEach(([key, file]) => {
        updatedFiles[`${scopeKeys[0]}_${key}`] = file;
      });
      setWorkingFiles(updatedFiles);

      partnerImagesField.onChange(scopeKeysForBackend);
      partnerFilesField.onChange(updatedFiles);
      partnerDirtyField.onChange(true);
    },
    [scopeKeys, scopeKeysForReading, selectedVariant, workingFiles, partnerImagesField, partnerFilesField, partnerDirtyField],
  );

  const handleStudioImagesChange = useCallback(
    (updatedImages: Record<string, string>, files: Record<string, File>) => {
      if (scopeKeys.length === 0 || !selectedVariant) return;

      setWorkingStudioImages((prev) => {
        const next = { ...prev };
        for (const scopeKey of scopeKeysForReading) {
          next[scopeKey] = { ...updatedImages };
        }
        return next;
      });

      const scopeKeysForBackend: ScopeImageMap = {};
      for (const scopeKey of scopeKeys) {
        scopeKeysForBackend[scopeKey] = { ...updatedImages };
      }

      // Compute updated files eagerly so we can pass them to onChange
      // (setWorkingStudioFiles is async, so workingStudioFiles would be stale).
      // Only drop entries whose image key was actually removed from
      // updatedImages — a blanket delete-then-re-add would discard files
      // staged by earlier uploads that aren't part of this call's `files`.
      const updatedStudioFiles = { ...workingStudioFiles };
      for (const scopeKey of scopeKeys) {
        for (const key of Object.keys(updatedStudioFiles)) {
          if (!key.startsWith(`${scopeKey}_`)) continue;
          const imageKey = key.slice(scopeKey.length + 1);
          if (!(imageKey in updatedImages)) delete updatedStudioFiles[key];
        }
      }
      Object.entries(files).forEach(([key, file]) => {
        updatedStudioFiles[`${scopeKeys[0]}_${key}`] = file;
      });
      setWorkingStudioFiles(updatedStudioFiles);

      studioImagesField.onChange(scopeKeysForBackend);
      studioFilesField.onChange(updatedStudioFiles);
      studioDirtyField.onChange(true);
    },
    [scopeKeys, scopeKeysForReading, selectedVariant, workingStudioFiles, studioImagesField, studioFilesField, studioDirtyField],
  );

  const handleVideoChange = useCallback(
    (updatedVideos: Record<string, string>) => {
      if (scopeKeys.length === 0 || !selectedVariant) return;

      setWorkingVideos((prev) => {
        const next = { ...prev };
        for (const scopeKey of scopeKeysForReading) {
          next[scopeKey] = { ...updatedVideos };
        }
        return next;
      });

      const scopeKeysForBackend: ScopeImageMap = {};
      for (const scopeKey of scopeKeys) {
        scopeKeysForBackend[scopeKey] = { ...updatedVideos };
      }

      videoImagesField.onChange(scopeKeysForBackend);
      videoDirtyField.onChange(true);
    },
    [scopeKeys, scopeKeysForReading, selectedVariant, videoImagesField, videoDirtyField],
  );

  const handleDeleteImage = useCallback(
    (key: string, storageType: 'catalog' | 'studio' | 'video' = 'catalog') => {
      if (storageType === 'studio') {
        const currentImages = mergeImages(scopeKeysForReading, workingStudioImages);
        delete currentImages[key];
        handleStudioImagesChange(currentImages, {});
      } else if (storageType === 'video') {
        const currentVideos = mergeImages(scopeKeysForReading, workingVideos);
        delete currentVideos[key];
        handleVideoChange(currentVideos);
      } else {
        const currentImages = mergeImages(scopeKeysForReading, workingImages);
        delete currentImages[key];
        handleImagesChange(currentImages, {});
      }
    },
    [scopeKeysForReading, workingImages, workingStudioImages, workingVideos, handleImagesChange, handleStudioImagesChange, handleVideoChange],
  );

  const handleUploadForKey = useCallback(
    (key: string, file: File, storageType: 'catalog' | 'studio' = 'catalog') => {
      const blobUrl = createBlobUrl(file);
      if (storageType === 'studio') {
        const currentImages = mergeImages(scopeKeysForReading, workingStudioImages);
        handleStudioImagesChange({ ...currentImages, [key]: blobUrl }, { [key]: file });
      } else {
        const currentImages = mergeImages(scopeKeysForReading, workingImages);
        handleImagesChange({ ...currentImages, [key]: blobUrl }, { [key]: file });
      }
    },
    [scopeKeysForReading, workingImages, workingStudioImages, handleImagesChange, handleStudioImagesChange, createBlobUrl],
  );

  return {
    scopeKeys,
    allImages,
    allStudioImages,
    allVideos,
    eligibleSlots,
    ineligibleAssets,
    handleImagesChange,
    handleStudioImagesChange,
    handleVideoChange,
    handleDeleteImage,
    handleUploadForKey,
    createBlobUrl,
  };
}
